import React, { useEffect, useRef, useState } from 'react';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import Checkbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import BusinessIcon from '@mui/icons-material/Business';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import { GetCompanyInfo, CompanyKeyName } from '../../../services/apiTypes';
import { IconButton } from '@mui/material';

interface SimpleTreeViewProps {
  selectedCompanyKey?: string;
  setSelectedCompany: (company: string) => void;
}

const StyledTreeItem = styled(TreeItem)(() => ({
  '& .MuiTreeItem-content': {
    display: 'flex',
    alignItems: 'center',
  },
}));

const rootCompany: CompanyKeyName = {
  companyKey: 'root',
  companyName: 'root',
};

/** 組織ツリー */
const UserOrganizationTree: React.FC<SimpleTreeViewProps> = ({
  selectedCompanyKey,
  setSelectedCompany,
}) => {
  const { t } = useTranslation();

  const [checkedKey, setCheckedKey] = useState<string>('');

  const { accountsInfoData } = useSelector(
    (state: RootState) => state.accountsInfo,
  );

  /** 親をすべて取得 */
  const findAllParents = (
    data: GetCompanyInfo[],
    targetKey: string,
  ): string[] => {
    const path: string[] = [];
    const nodeMap = new Map<string, GetCompanyInfo>();
    data.forEach((item) => nodeMap.set(item.key, item));

    let currentKey: string | undefined = targetKey;
    while (currentKey) {
      const currentNode = nodeMap.get(currentKey);
      if (!currentNode) break;
      path.push(currentNode.key);
      if (
        !currentNode.primaryAgency ||
        currentNode.primaryAgency.companyKey === currentNode.key
      )
        break;
      currentKey = currentNode.primaryAgency.companyKey;
    }
    return path;
  };

  /** 展開されているキー */
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  /** 展開状態の変更 */
  const handleExpandedItemsChange = (
    _: React.SyntheticEvent | null,
    itemIds: string[],
  ) => {
    setExpandedItems(itemIds);
  };

  /** チェックボックスの変更 */
  const handleToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
    nodeId: string,
  ) => {
    setCheckedKey(event.target.checked ? nodeId : '');
  };

  const renderTree = (nodes: GetCompanyInfo[], parentKey: string | null) => {
    const normalizedNodes = nodes.map((node) =>
      node.key === node.primaryAgency.companyKey ||
      !nodes.some((node2) => node2.key === node.primaryAgency.companyKey)
        ? { ...node, primaryAgency: rootCompany }
        : node,
    );

    return normalizedNodes
      .filter((node) => node.primaryAgency.companyKey === parentKey)
      .map((node) => (
        <StyledTreeItem
          key={node.key}
          itemId={node.key}
          label={
            <div
              ref={(el) => {
                itemRefs.current[node.key] = el;
              }}
            >
              <Checkbox
                checked={checkedKey === node.key}
                onChange={(event) => handleToggle(event, node.key)}
              />
              {node.name}
            </div>
          }
        >
          {renderTree(nodes, node.key)}
        </StyledTreeItem>
      ));
  };

  const [isOpenCompanyDialog, setIsOpenCompanyDialog] = useState(false);
  const handleOpen = () => setIsOpenCompanyDialog(true);
  const handleClose = () => setIsOpenCompanyDialog(false);
  const handleOk = () => {
    setSelectedCompany(checkedKey);
    setIsOpenCompanyDialog(false);
  };

  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const [firstLoad, setFirstLoad] = useState(true);

  /** ダイアログが開いたときに選択状態を設定*/
  useEffect(() => {
    if (isOpenCompanyDialog && selectedCompanyKey) {
      setExpandedItems(
        findAllParents(accountsInfoData.companyList, selectedCompanyKey),
      );
      setCheckedKey(selectedCompanyKey);
    } else {
      setExpandedItems([]);
      setCheckedKey('');
    }
    if (!isOpenCompanyDialog) setFirstLoad(true);
  }, [isOpenCompanyDialog, selectedCompanyKey]);

  /** 初回読み込み時にスクロール */
  useEffect(() => {
    if (isOpenCompanyDialog && firstLoad && checkedKey !== '') {
      const el = itemRefs.current[checkedKey];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setFirstLoad(false);
      }
    }
  }, [isOpenCompanyDialog, checkedKey]);

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          color: 'primary.main',
        }}
        size="small"
      >
        <BusinessIcon />
      </IconButton>

      <Dialog
        open={isOpenCompanyDialog}
        onClose={handleClose}
        closeAfterTransition={false}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('MSG_SELECT_COMPANY')}</DialogTitle>
        <DialogContent dividers style={{ height: '400px', overflowY: 'auto' }}>
          <SimpleTreeView
            expandedItems={expandedItems}
            onExpandedItemsChange={handleExpandedItemsChange}
          >
            {renderTree(accountsInfoData.companyList, 'root')}
          </SimpleTreeView>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            sx={{ mx: 2, my: 1, width: '120px' }}
            variant="contained"
            color="inherit"
          >
            {t('BTN_CANCEL')}
          </Button>
          <Button
            onClick={handleOk}
            sx={{ mx: 2, my: 1, width: '120px' }}
            variant="contained"
            color="primary"
            disabled={checkedKey === ''}
          >
            {t('BTN_OK')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserOrganizationTree;
